/**
 * Likes Store
 *
 * Manages liked songs with IndexedDB persistence.
 * Uses Zustand for reactive state + Dexie for persistence.
 */

import { create } from 'zustand';
import type { Song } from '@zemer/shared';
import { db, type LikedSongEntity } from '@/db/database';

export interface LikedSong {
  id: string;
  title: string;
  artists?: Array<{ id?: string; name: string }>;
  thumbnail?: string;
  thumbnails?: Array<{ url: string; width: number; height: number }>;
  likedAt: number;
}

interface LikesState {
  likedSongs: LikedSong[];
  isLoading: boolean;
  isLoaded: boolean;

  // Load from IndexedDB
  loadLikes: () => Promise<void>;

  // Check if song is liked
  isLiked: (songId: string) => boolean;

  // Toggle like status
  toggleLike: (song: Song) => Promise<void>;

  // Unlike a song
  unlike: (songId: string) => Promise<void>;

  // Get all liked songs
  getLikedSongs: () => LikedSong[];
}

// Convert DB entity to LikedSong
function entityToLikedSong(entity: LikedSongEntity): LikedSong {
  return {
    id: entity.id,
    title: entity.title,
    artists: entity.artistsJson ? JSON.parse(entity.artistsJson) : undefined,
    thumbnail: entity.thumbnail,
    thumbnails: entity.thumbnailsJson ? JSON.parse(entity.thumbnailsJson) : undefined,
    likedAt: entity.likedAt,
  };
}

// Convert LikedSong to DB entity
function likedSongToEntity(song: LikedSong): LikedSongEntity {
  return {
    id: song.id,
    title: song.title,
    artistsJson: JSON.stringify(song.artists || []),
    thumbnail: song.thumbnail,
    thumbnailsJson: JSON.stringify(song.thumbnails || []),
    likedAt: song.likedAt,
  };
}

export const useLikesStore = create<LikesState>((set, get) => ({
  likedSongs: [],
  isLoading: false,
  isLoaded: false,

  loadLikes: async () => {
    if (get().isLoaded) return;

    set({ isLoading: true });
    try {
      const entities = await db.likedSongs.orderBy('likedAt').reverse().toArray();
      const likedSongs = entities.map(entityToLikedSong);
      set({ likedSongs, isLoaded: true, isLoading: false });
      console.log(`[Likes] Loaded ${likedSongs.length} liked songs from DB`);
    } catch (error) {
      console.error('[Likes] Failed to load:', error);
      set({ isLoading: false, isLoaded: true });
    }
  },

  isLiked: (songId: string) => {
    return get().likedSongs.some((s) => s.id === songId);
  },

  toggleLike: async (song: Song) => {
    const { likedSongs, isLiked } = get();

    if (isLiked(song.id)) {
      // Unlike
      set({
        likedSongs: likedSongs.filter((s) => s.id !== song.id),
      });
      try {
        await db.likedSongs.delete(song.id);
      } catch (error) {
        console.error('[Likes] Failed to unlike:', error);
      }
    } else {
      // Like
      const likedSong: LikedSong = {
        id: song.id,
        title: song.title,
        artists: song.artists,
        thumbnail: (song as any).thumbnail,
        thumbnails: song.thumbnails,
        likedAt: Date.now(),
      };
      set({
        likedSongs: [likedSong, ...likedSongs],
      });
      try {
        await db.likedSongs.put(likedSongToEntity(likedSong));
      } catch (error) {
        console.error('[Likes] Failed to save like:', error);
      }
    }
  },

  unlike: async (songId: string) => {
    set({
      likedSongs: get().likedSongs.filter((s) => s.id !== songId),
    });
    try {
      await db.likedSongs.delete(songId);
    } catch (error) {
      console.error('[Likes] Failed to unlike:', error);
    }
  },

  getLikedSongs: () => {
    return get().likedSongs;
  },
}));

// Auto-load likes on module init
useLikesStore.getState().loadLikes();
